/* eslint-disable no-undef */
/* eslint-disable no-console */
/* eslint-disable max-len */
import { Box, Grid, Stack, Tooltip } from '@mui/material';
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { IoIosPause, IoIosPlay } from 'react-icons/io';
import { MdStopCircle } from 'react-icons/md';
import { RxSpeakerLoud } from 'react-icons/rx';
import { LanguageContext } from '../../../../../../libs/Context';
import ReactDOM from 'react-dom/client';
import SpeakingAnimation from '../../../Reusable/SpeakingAnimation';

// Global state for the currently playing utterance
let currentUtterance:any = null;
let cachedVoices: SpeechSynthesisVoice[] = [];
let voicesLoaded = false;

type SpeechStates= 'playing' | 'paused' | '';

const ReadDescriptionButton = (props) =>
{
	const { feed, getSpeechInfo, speechEndCallback } = props;
	const { language } = useContext(LanguageContext);
	const [ speechState, setSpeechState ] = useState<SpeechStates>('');

	// Load and cache voices properly
	const loadVoices = useCallback(() =>
	{
		return new Promise<SpeechSynthesisVoice[]>((resolve) =>
		{
			if (voicesLoaded && cachedVoices.length > 0)
			{
				resolve(cachedVoices);

				return;
			}

			const getVoices = () =>
			{
				const voices = window.speechSynthesis.getVoices();

				if (voices.length > 0)
				{
					cachedVoices = voices;
					voicesLoaded = true;
					resolve(voices);
				}
				else
				{
					// If voices not loaded yet, wait and try again
					setTimeout(getVoices, 100);
				}
			};

			getVoices();
		});
	}, []);

	// const descWithoutEmoji = feed?.description;

	// const descWithoutEmoji = feed?.description;
	const descWithoutEmoji = useMemo(() =>
	{
		const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
		const newLineRegex = /<\/p><p>/g;
		const descText =
					(feed?.description || feed?.repostObj?.description || '')
						.replace(emojiRegex, '')
						.replace(newLineRegex, ' ');
		const parser = new DOMParser();

		return parser.parseFromString(descText, 'text/html').body.textContent?.trim() || '';
	}, [ feed?.description ]);

	// Cancel any active speech immediately
	const cancelCurrentSpeech = useCallback(() =>
	{
		if (currentUtterance)
		{
			window.speechSynthesis.cancel();
			currentUtterance = null;
		}

		setSpeechState('');
	}, []);

	let root:any = null;
	// Handle Speaker Click: Speak description
	const handleSpeakerClick = useCallback(async () =>
	{
		if (!descWithoutEmoji) return;

		// Force cancel any existing speech immediately
		cancelCurrentSpeech();

		// Load voices first to ensure consistency
		const voices = await loadVoices();

		// Introduce a slight delay to ensure the queue resets
		setTimeout(() =>
		{
			const utterance = new SpeechSynthesisUtterance(descWithoutEmoji);

			// Ensure language is a string and has a fallback
			const currentLanguage = typeof language === 'string' ? language : 'en';
			const languageCode = currentLanguage.split('-')[0] || 'en';

			utterance.lang = currentLanguage;

			// Attempt to find a female voice; fallback to the first available voice
			const femaleVoice = voices.find((voice) =>
				voice.name.toLowerCase().includes('female') &&
				voice.localService &&
				voice.lang.startsWith(languageCode) // Match language
			);

			// Fallback to any voice that matches the language
			const languageVoice = voices.find((voice) =>
				voice.lang.startsWith(languageCode)
			);

			utterance.voice = femaleVoice || languageVoice || voices[0];

			// Debug logging for voice selection
			console.log('🎤 Voice Selection Debug:', {
				totalVoices        : voices.length,
				selectedVoice      : utterance.voice?.name,
				voiceLanguage      : utterance.voice?.lang,
				femaleVoiceFound   : Boolean(femaleVoice),
				languageVoiceFound : Boolean(languageVoice),
				requestedLanguage  : currentLanguage,
				languageCode,
				originalLanguage   : language
			});

			// Store the utterance globally or in a shared state
			currentUtterance = utterance;
			utterance.volume = 0.8;

			// Attach event handlers
			utterance.onstart = () =>
			{
				setSpeechState('playing');
				const speakerIconParent = document.getElementById(`description_speak_icon_${feed?._id}`);

				if (speakerIconParent)
				{
					root = ReactDOM.createRoot(speakerIconParent);

					root && root.render(
						<Grid item display='flex' pr={1.5}>
							<SpeakingAnimation/>
						</Grid>);
				}
			};
			utterance.onend = () =>
			{
				speechEndCallback();
				root && root?.unmount();
				setSpeechState('');
				currentUtterance = null;
			};
			utterance.onerror = (e) =>
			{
				speechEndCallback();
				root && root?.unmount();
				console.error('Speech synthesis error:', e);
				setSpeechState('');
				currentUtterance = null;
			};

			// Track currently spoken words
			utterance.onboundary = (event) =>
			{
				if (event.name === 'word')
				{
					getSpeechInfo(event);
				}
			};

			// Start speaking
			window.speechSynthesis.speak(utterance);
		}, 50); // Delay ensures proper queue clearing
	}, [ descWithoutEmoji, language, cancelCurrentSpeech, loadVoices ]);

	// Pause or Resume current speech
	const handlePause = useCallback(() =>
	{
		if (speechState === 'playing')
		{
			window.speechSynthesis.pause();
			setSpeechState('paused');
		}
		else if (speechState === 'paused')
		{
			window.speechSynthesis.resume();
			setSpeechState('playing');
		}
	}, [ speechState ]);

	// Stop current speech
	const handleStop = useCallback(() =>
	{
		cancelCurrentSpeech();
	}, [ cancelCurrentSpeech ]);

	// Load voices on component mount
	useEffect(() =>
	{
		const loadVoicesOnMount = () =>
		{
			const voices = window.speechSynthesis.getVoices();

			if (voices.length > 0)
			{
				cachedVoices = voices;
				voicesLoaded = true;
			}
		};

		// Try to load voices immediately
		loadVoicesOnMount();

		// Also listen for voices changed event
		window.speechSynthesis.addEventListener('voiceschanged', loadVoicesOnMount);

		return () =>
		{
			window.speechSynthesis.removeEventListener('voiceschanged', loadVoicesOnMount);
		};
	}, []);

	// Cleanup on unmount
	useEffect(() =>
	{
		return () => cancelCurrentSpeech();
	}, [ cancelCurrentSpeech ]);

	if (!descWithoutEmoji) return <></>;

	const isPlayingOrPaused = [ 'playing', 'paused' ].includes(speechState);

	return (
		<Tooltip title={isPlayingOrPaused ? null : language.readforme} placement='top'>
			<Box
				display='flex'
				sx={{ marginRight : 0.5,
					// cursor      : 'pointer',
					...(!descWithoutEmoji ? { pointerEvents: 'none', opacity: 0.3 } : {}),
					color       : '#536471',
					'&:hover'   : {

						'& svg' : {
							backgroundColor : 'transparent',
							color           : '#23A4C4',
							transform       : 'scale(1.2)'
						}
					}
				}}
			>
				{isPlayingOrPaused
					? (
						<Stack
							direction='row'
							spacing={3}
							p='var(--pixel8)'
							sx={{
								boxShadow    : '0px 0px 5px 0px #00000040',
								borderRadius : 'var(--pixel8)',
								overflow     : 'hidden'
							}}
						>
							<RxSpeakerLoud fontSize='var(--pixel18)' color='var(--primaryColor)' />
							{speechState === 'paused'
								? (
									<IoIosPlay
										fontSize='var(--pixel18)'
										color='var(--blackColor)'
										onClick={handlePause}
										style={{ cursor: 'pointer' }}
									/>
								)
								: (
									<IoIosPause
										fontSize='var(--pixel18)'
										color='var(--blackColor)'
										onClick={handlePause}
										style={{ cursor: 'pointer' }}
									/>
								)}
							<MdStopCircle
								fontSize='var(--pixel18)'
								color='var(--redColor)'
								onClick={handleStop}
								style={{ cursor: 'pointer' }}
							/>
						</Stack>
					)
					: (
						<RxSpeakerLoud
							fontSize='var(--pixel18)'
							onClick={handleSpeakerClick}
							style={isPlayingOrPaused ? {} : { cursor: 'pointer' }}
						/>
					)}
			</Box>
		</Tooltip>
	);
};

export default ReadDescriptionButton;